import React from 'react';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'i18n';
import { useUserApi } from 'services/user-service';
import Form from 'components/form';
import AuthContainer from 'routes/auth/components/container';
import Password from 'routes/auth/components/password';
import { WithMargin } from 'components/layout';
import {
  FormHalfWidthContainer,
  FormBottomContainer,
  SignInCodeGiftIcon,
  RegistrationSuccessModal,
  OptionLink,
  SubmitButton,
} from 'routes/auth/register/style';
import { InnerFormHolderBottom } from 'routes/auth/components/container/style';
import { OptionText } from 'routes/auth/components/container/style';
import { FormTextInput } from 'routes/auth/style';
import { object, string, boolean, ref } from 'yup';
import { toast } from 'services/toast';
import { typography } from 'styles/settings';
import { notEmpty } from 'utils/js';

function createValidationSchema(t) {
  return object().shape({
    firstName: string().required(t('First name is required.')),
    lastName: string().required(t('Last name is required.')),
    email: string()
      .email(t('Invalid email format'))
      .required(t('Email is required')),
    password: string()
      .required(t('No password provided'))
      .min(8, t('Password is too short - should be 8 chars minimum.')),
    confirmPassword: string().test(
      'password-match-test',
      t('Passwords must match'),
      function (value) {
        return this.resolve(ref('password')) === value;
      },
    ),
    agreedToTerms: boolean()
      .required(t('You have to accept our terms of service'))
      .oneOf([true], t('You have to accept our terms of service')),
  });
}

function Register() {
  const modal = React.useRef<any>(null);

  const { t } = useTranslation();
  const userApi = useUserApi();
  const history = useHistory();
  const location = useLocation();

  const code = new URLSearchParams(location.search).get('code') || '';

  const validationSchema = React.useMemo(() => createValidationSchema(t), []);

  async function onSubmit({ email, password, firstName, lastName, code }) {
    // const checkCodeResponse = await userApi.checkIfCodeIsUsed(code);

    // if (checkCodeResponse.isFailure) {
    //   return toast.error(checkCodeResponse.getErrorMessage());
    // }

    const registerResponse = await userApi.register({
      email,
      password,
      firstName,
      lastName,
    });

    if (registerResponse.isFailure) {
      return toast.error(registerResponse.getErrorMessage());
    }

    // const markCodeResponse = await userApi.markCodeAsUsed(code, email);

    // if (markCodeResponse.isFailure) {
    //   return toast.error(markCodeResponse.getErrorMessage());
    // }

    return modal.current.show();
  }

  function optionLabelRenderer() {
    return (
      <OptionText>
        {t('I agree to the')}
        &nbsp;
        <OptionLink
          onClick={() =>
            window.open(process.env.REACT_APP_PRIVACY_URL, '_blank')
          }>
          {t('privacy policy')}
        </OptionLink>
      </OptionText>
    );
  }

  return (
    <>
      <RegistrationSuccessModal
        ref={modal}
        onHide={() => history.push('/login')}
      />
      <AuthContainer
        titleText={t('Register for a IMMO-OX account')}
        ctaLabelText={t('You already have a IMMO-OX account?')}
        ctaButtonText={t('Sign in')}
        onCtaButtonClick={() => history.push('/immo-ox/login')}>
        <Form
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            code,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}>
          {(form) => {
            return (
              <>
                <FormHalfWidthContainer>
                  <FormTextInput
                    name="firstName"
                    placeholder={t('First name')}
                  />
                  <FormTextInput name="lastName" placeholder={t('Last name')} />
                </FormHalfWidthContainer>
                <FormBottomContainer>
                  <FormTextInput
                    type="email"
                    name="email"
                    placeholder={t('E-Mail')}
                  />
                  <Password
                    name="password"
                    placeholder={t('Password')}
                    onBlur={(event) => {
                      if (event.target.value && form.values.confirmPassword) {
                        form.validateField('confirmPassword');
                        form.setFieldTouched('confirmPassword');
                      }
                    }}
                  />
                  <Password
                    name="confirmPassword"
                    placeholder={t('Confirm Password')}
                    onBlur={(event) => {
                      if (form.values.password && event.target.value) {
                        form.validateField('confirmPassword');
                        form.setFieldTouched('confirmPassword');
                      }
                    }}
                  />
                  {/* <WithMargin top={typography.size.md}>
                    <FormHalfWidthContainer>
                      <FormTextInput
                        name="code"
                        placeholder={t('Sign in code')}
                        iconRight={<SignInCodeGiftIcon />}
                        disabled={notEmpty(code)}
                      />
                    </FormHalfWidthContainer>
                  </WithMargin> */}
                </FormBottomContainer>
                <InnerFormHolderBottom>
                  <Form.Option
                    label={optionLabelRenderer}
                    name="agreedToTerms"
                  />
                  <WithMargin bottom={typography.size.md}>
                    <SubmitButton
                      label={t('Register')}
                      onClick={form.handleSubmit}
                    />
                  </WithMargin>
                </InnerFormHolderBottom>
              </>
            );
          }}
        </Form>
      </AuthContainer>
    </>
  );
}

export default Register;
